import {
  Link,
  Navigate,
  useLocation,
  useNavigate,
  useParams
} from 'react-router'
import { useQuery } from '@tanstack/react-query'
import { useId } from 'react'
import {
  AppLayout,
  Mtds,
  ScreenHeader,
  ScreenLayout,
  TopBar,
  useAuth
} from 'prove-common'
import { AppLogo } from '../components/app-logo/AppLogo'
import { SideBarLayout } from '../components/side-bar-layout/SideBarLayout'
import { AddNewNewsArticle } from '../components/add-new-news-article/AddNewNewsArticle'
import { newsApiError, newsUserMessage } from '../utils/newsApi'

export const NewsEditorScreen = () => {
  const { isAdmin } = useAuth()
  const { newsId } = useParams<{ newsId: string }>()
  return isAdmin ? (
    <NewsEditor key={newsId ?? 'new'} />
  ) : (
    <Navigate to='/unauthorized' replace />
  )
}

function NewsEditor() {
  const { newsId } = useParams<{ newsId: string }>()
  const { search } = useLocation()
  const { authFetch } = useAuth()
  const navigate = useNavigate()
  const editSession = useId()
  const articleId = newsId === undefined ? undefined : Number(newsId)
  const validId =
    articleId !== undefined &&
    /^\d+$/.test(newsId ?? '') &&
    Number.isSafeInteger(articleId) &&
    articleId > 0
  const returnTo = `/news${search}`
  const query = useQuery({
    // A unique key forces a fresh read for each editor mount without a
    // render-phase/effect state mirror. The editor itself preserves its draft
    // if this query later refetches.
    queryKey: ['newsArticle', articleId, editSession],
    enabled: validId,
    retry: false,
    refetchOnMount: 'always',
    queryFn: async () => {
      const result = await authFetch().GET('/news/{articleId}', {
        params: { path: { articleId: articleId! } }
      })
      if (!result.response.ok || result.error) throw newsApiError(result.error)
      if (!result.data) throw new Error('Artikkelen finnes ikke.')
      return result.data
    }
  })

  return (
    <AppLayout
      sidebar={<SideBarLayout />}
      useMobileBar={false}
      isFullWidth
      topBar={<TopBar logo={<AppLogo />} title='Nyheter' />}
    >
      <ScreenLayout
        header={
          <ScreenHeader
            title={newsId === undefined ? 'Ny artikkel' : 'Rediger artikkel'}
            actions={
              <Mtds.Button as={Link} to={returnTo} data-variant='secondary'>
                Til nyhetslisten
              </Mtds.Button>
            }
          />
        }
      >
        {newsId !== undefined && !validId ? (
          <Mtds.Alert data-color='danger'>Ugyldig artikkel-ID.</Mtds.Alert>
        ) : validId && !query.data ? (
          query.isError ? (
            <Mtds.Alert as='div' role='alert' data-color='danger'>
              <Mtds.Heading as='h2' data-size='sm'>
                Kunne ikke åpne artikkelen
              </Mtds.Heading>
              <p>{newsUserMessage(query.error)}</p>
              <Mtds.Button
                type='button'
                data-variant='secondary'
                onClick={() => void query.refetch()}
              >
                Prøv igjen
              </Mtds.Button>
            </Mtds.Alert>
          ) : (
            <p role='status'>Laster artikkel …</p>
          )
        ) : (
          <AddNewNewsArticle
            key={newsId ?? 'new'}
            article={query.data}
            returnTo={returnTo}
            onSuccess={() => void navigate(returnTo, { replace: true })}
          />
        )}
      </ScreenLayout>
    </AppLayout>
  )
}
